import React, { useEffect, useRef, useState } from 'react';
import './Connect.css';
import { buildCameraPreviewDataUrl } from '../utils/cameraPreview';
import { parseWifiTelemetryPayload } from '../utils/wifiTelemetry';
import { queueDeviceOperation, startOperationSync } from '../utils/operationQueue';
import { removeDeviceTelemetry, upsertDeviceTelemetry } from '../utils/deviceTelemetry';
import { createCanvasStream, createRelaySender } from '../utils/streamRelay';

const CONNECT_BUILD_STAMP = process.env.REACT_APP_CONNECT_BUILD_STAMP || 'connect-ui-2026-07-23-1';
const BLE_SERVICE_UUID = '4fafc201-1fb5-459e-8fcc-c5c9c331914b';
const BLE_CHARACTERISTIC_UUID = 'beb5483e-36e1-4688-b7f5-ea07361b26a8';
const WIFI_SSID_UUID = '4fafc202-1fb5-459e-8fcc-c5c9c331914b';
const WIFI_PASSWORD_UUID = '4fafc203-1fb5-459e-8fcc-c5c9c331914b';
const WIFI_APPLY_UUID = '4fafc204-1fb5-459e-8fcc-c5c9c331914b';
const WIFI_STATUS_UUID = '4fafc205-1fb5-459e-8fcc-c5c9c331914b';
const APP_AUTH_TOKEN_KEY = 'auth_token';
const CONNECT_GUEST_TOKEN_KEY = 'connect_guest_token';
const CONNECT_GUEST_ORG_KEY = 'connect_guest_organization_code';
const CAMERA_URL_STORAGE_KEY = 'remu_bucket_camera_url_v1';
const WIFI_TELEMETRY_URL_STORAGE_KEY = 'remu_bucket_wifi_telemetry_url_v1';
const WIFI_SERIAL_NUMBER_STORAGE_KEY = 'remu_bucket_wifi_serial_number_v1';
const WIFI_SESSION_START_STORAGE_KEY = 'remu_bucket_wifi_session_start_v1';
// app_httpd.cpp starts the MJPEG stream on a separate port (control port + 1).
const DEFAULT_CAMERA_URL = 'http://192.168.4.2:81/stream';
const DEFAULT_WIFI_TELEMETRY_URL = 'http://192.168.4.1/telemetry';

// Safari/WebKit (iOS) cannot render multipart/x-mixed-replace, so the "open in
// new tab" fallback targets the camera's own /view page (port 80), which polls
// single JPEG snapshots instead of the raw MJPEG stream.
const ensureUrlScheme = (url) => (/^https?:\/\//i.test(url) ? url : `http://${url}`);

const deriveCameraViewUrl = (streamUrl) => {
  try {
    const parsed = new URL(ensureUrlScheme(streamUrl));
    parsed.port = '';
    parsed.pathname = '/view';
    parsed.search = '';
    return parsed.toString();
  } catch (error) {
    return streamUrl;
  }
};

function Connect() {
  const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
      return process.env.REACT_APP_API_BASE_URL;
    }

    return '/api/v1';
  };

  const parseJsonResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(`Expected JSON response but received: ${text.slice(0, 80)}`);
    }

    return response.json();
  };

  const getOrganizationGuestToken = async (organizationCode) => {
    const loginResponse = await fetch(`${getApiBaseUrl()}/login/organization`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_code: organizationCode })
    });

    const loginData = await parseJsonResponse(loginResponse);
    if (!loginResponse.ok || !loginData.token) {
      throw new Error(loginData.error || 'Failed to obtain organization token');
    }

    localStorage.setItem(CONNECT_GUEST_TOKEN_KEY, loginData.token);
    localStorage.setItem(CONNECT_GUEST_ORG_KEY, organizationCode);
    return loginData.token;
  };

  const getScopedToken = async (organizationCode) => {
    const appToken = localStorage.getItem(APP_AUTH_TOKEN_KEY);
    if (appToken) {
      return appToken;
    }

    const cachedGuestToken = localStorage.getItem(CONNECT_GUEST_TOKEN_KEY);
    const cachedOrganizationCode = localStorage.getItem(CONNECT_GUEST_ORG_KEY);
    if (cachedGuestToken && cachedOrganizationCode === organizationCode) {
      return cachedGuestToken;
    }

    return getOrganizationGuestToken(organizationCode);
  };

  const fetchWithAuth = async (url, options = {}, tokenOverride) => {
    const token = tokenOverride || localStorage.getItem(APP_AUTH_TOKEN_KEY) || localStorage.getItem(CONNECT_GUEST_TOKEN_KEY);

    if (!token) {
      throw new Error('No token available for authenticated request');
    }

    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`
      }
    });
  };

  const [organizationCode, setOrganizationCode] = useState('');
  const [organizationLookupLoading, setOrganizationLookupLoading] = useState(false);
  const [organizationLookupMessage, setOrganizationLookupMessage] = useState('');
  const [organizationLookupError, setOrganizationLookupError] = useState('');
  const [foundCustomerId, setFoundCustomerId] = useState(null);
  const [bucketTypes, setBucketTypes] = useState([]);
  const [selectedBucketType, setSelectedBucketType] = useState('');
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [selectedSerialNumber, setSelectedSerialNumber] = useState('');
  const [bucketTypesLoading, setBucketTypesLoading] = useState(false);
  const [serialNumbersLoading, setSerialNumbersLoading] = useState(false);
  const [bucketDataError, setBucketDataError] = useState('');
  const [bleStatus, setBleStatus] = useState('Not connected');
  const [bleError, setBleError] = useState('');
  const [bleDeviceName, setBleDeviceName] = useState('');
  const [bleConnected, setBleConnected] = useState(false);
  const [bleTelemetry, setBleTelemetry] = useState('');
  const [bleParsedTelemetry, setBleParsedTelemetry] = useState({ temperature: '', pressure: '' });
  const [bleConnecting, setBleConnecting] = useState(false);
  const [bleDisconnecting, setBleDisconnecting] = useState(false);
  const [bleReadCount, setBleReadCount] = useState(0);
  const [wifiProvisionSsid, setWifiProvisionSsid] = useState('');
  const [wifiProvisionPassword, setWifiProvisionPassword] = useState('');
  const [wifiProvisionStatus, setWifiProvisionStatus] = useState('');
  const [wifiProvisioning, setWifiProvisioning] = useState(false);
  const [scopedAuthToken, setScopedAuthToken] = useState(null);
  const [cameraUrl, setCameraUrl] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_CAMERA_URL;
    }

    return localStorage.getItem(CAMERA_URL_STORAGE_KEY) || DEFAULT_CAMERA_URL;
  });
  const [wifiTelemetryUrl, setWifiTelemetryUrl] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_WIFI_TELEMETRY_URL;
    }

    return localStorage.getItem(WIFI_TELEMETRY_URL_STORAGE_KEY) || DEFAULT_WIFI_TELEMETRY_URL;
  });
  const [streamVisible, setStreamVisible] = useState(false);
  const [streamStatus, setStreamStatus] = useState('idle');
  const [streamAttempt, setStreamAttempt] = useState(0);
  const [recordingStatus, setRecordingStatus] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [wifiTelemetryStatus, setWifiTelemetryStatus] = useState('idle');
  const [connectionMode, setConnectionMode] = useState('ble');
  const [wifiModalOpen, setWifiModalOpen] = useState(false);
  const [wifiConnectionActive, setWifiConnectionActive] = useState(false);
  const [relaySharing, setRelaySharing] = useState(false);
  const [relayStatus, setRelayStatus] = useState('');
  const [relayViewerCount, setRelayViewerCount] = useState(0);

  const relaySenderRef = useRef(null);
  const relayCanvasRef = useRef(null);

  const bleDeviceRef = useRef(null);
  const bleCharacteristicRef = useRef(null);
  const wifiApplyCharacteristicRef = useRef(null);
  const wifiStatusCharacteristicRef = useRef(null);
  const blePollingTimerRef = useRef(null);
  const bleConnectionRef = useRef({ serialNumber: '', deviceName: '' });
  const bleSessionStartRef = useRef('');
  const wifiSessionStartRef = useRef('');
  const lastCameraSnapshotRef = useRef('');
  const lastRecordingUrlRef = useRef('');
  const bleSupported = typeof navigator !== 'undefined' && Boolean(navigator.bluetooth);

  useEffect(() => startOperationSync(), []);

  const logDeviceOperation = ({ operationStart, operationEnd, notes, source }) => {
    const { serialNumber } = bleConnectionRef.current;
    if (!serialNumber || !foundCustomerId) {
      return;
    }

    queueDeviceOperation({
      serial_number: serialNumber,
      customer_id: foundCustomerId,
      organization_code: organizationCode,
      operation_start: operationStart,
      operation_end: operationEnd,
      notes: notes || bleTelemetry,
      temperature_summary: bleParsedTelemetry.temperature || '',
      pressure_summary: bleParsedTelemetry.pressure || '',
      camera_snapshot_data: lastCameraSnapshotRef.current || '',
      recording_url: lastRecordingUrlRef.current || '',
      source
    });
  };

  const cacheOrganizationCode = async (orgCode, customerId) => {
    const cacheData = {
      organization_code: orgCode,
      customer_id: customerId,
      cached_at: new Date().toISOString()
    };

    localStorage.setItem('organization_code', orgCode);
    localStorage.setItem('customer_id', String(customerId));
    localStorage.setItem('customer_context', JSON.stringify(cacheData));

    if (typeof window !== 'undefined' && 'caches' in window) {
      const cache = await caches.open('remu-pwa-cache-v1');
      await cache.put(
        '/pwa/customer-context.json',
        new Response(JSON.stringify(cacheData), {
          headers: { 'Content-Type': 'application/json' }
        })
      );
    }
  };

  const publishLiveTelemetryToApi = async ({
    serialNumber,
    deviceName,
    connected,
    status,
    telemetry,
    parsedTelemetry,
    readCount,
    updatedAt,
    cameraSnapshotData,
    recordingUrl
  }) => {
    if (!serialNumber) {
      return;
    }

    const token = scopedAuthToken || localStorage.getItem(APP_AUTH_TOKEN_KEY) || localStorage.getItem(CONNECT_GUEST_TOKEN_KEY);
    if (!token) {
      return;
    }

    try {
      await fetchWithAuth(
        `${getApiBaseUrl()}/telemetry/live`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            serial_number: serialNumber,
            customer_id: foundCustomerId,
            organization_code: organizationCode,
            device_name: deviceName,
            connected,
            status,
            telemetry,
            temperature: parsedTelemetry?.temperature || '',
            pressure: parsedTelemetry?.pressure || '',
            camera_url: ensureUrlScheme(cameraUrl || DEFAULT_CAMERA_URL),
            camera_snapshot_data: cameraSnapshotData || '',
            recording_url: recordingUrl || '',
            read_count: Number(readCount || 0),
            updated_at: updatedAt || new Date().toISOString()
          })
        },
        token
      );
    } catch (error) {
      // Live relay is best-effort and should not block BLE streaming.
    }
  };

  const resetBleState = () => {
    if (blePollingTimerRef.current) {
      clearInterval(blePollingTimerRef.current);
      blePollingTimerRef.current = null;
    }

    setBleConnected(false);
    setBleStatus('Not connected');
    setBleError('');
    setBleDeviceName('');
    setBleTelemetry('');
    setBleParsedTelemetry({ temperature: '', pressure: '' });
    setBleReadCount(0);
  };

  const clearSharedBleDeviceTelemetry = () => {
    const { serialNumber, deviceName } = bleConnectionRef.current;

    if (serialNumber) {
      const disconnectedAt = new Date().toISOString();

      void publishLiveTelemetryToApi({
        serialNumber,
        deviceName,
        connected: false,
        status: 'Disconnected',
        telemetry: bleTelemetry,
        parsedTelemetry: bleParsedTelemetry,
        readCount: bleReadCount,
        updatedAt: disconnectedAt
      });

      removeDeviceTelemetry(serialNumber);
    }

    bleConnectionRef.current = { serialNumber: '', deviceName: '' };
  };

  const syncSharedBleDeviceTelemetry = (overrides = {}) => {
    const { serialNumber, deviceName } = bleConnectionRef.current;

    if (!serialNumber) {
      return;
    }

    const syncedTelemetry = {
      serialNumber,
      deviceName,
      customerId: foundCustomerId,
      organizationCode,
      connected: overrides.connected !== false,
      status: overrides.status || bleStatus || 'Connected',
      telemetry: overrides.telemetry ?? bleTelemetry,
      cameraUrl: overrides.cameraUrl ?? ensureUrlScheme(cameraUrl || DEFAULT_CAMERA_URL),
      cameraSnapshotData: overrides.cameraSnapshotData ?? '',
      recordingUrl: overrides.recordingUrl ?? '',
      parsedTelemetry: overrides.parsedTelemetry || bleParsedTelemetry,
      readCount: overrides.readCount ?? bleReadCount,
      updatedAt: new Date().toISOString()
    };

    upsertDeviceTelemetry(syncedTelemetry);

    void publishLiveTelemetryToApi({
      serialNumber,
      deviceName,
      connected: syncedTelemetry.connected,
      status: syncedTelemetry.status,
      telemetry: syncedTelemetry.telemetry,
      parsedTelemetry: syncedTelemetry.parsedTelemetry,
      readCount: syncedTelemetry.readCount,
      updatedAt: syncedTelemetry.updatedAt,
      cameraSnapshotData: syncedTelemetry.cameraSnapshotData,
      recordingUrl: syncedTelemetry.recordingUrl
    });
  };

  const decodeBlePayload = (value) => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof TextDecoder !== 'undefined' && value instanceof DataView) {
      return new TextDecoder().decode(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength));
    }

    if (value instanceof ArrayBuffer) {
      return new TextDecoder().decode(value);
    }

    return '';
  };

  const fetchLocalWifiTelemetry = async () => {
    if (!wifiTelemetryUrl || bleConnected || bleDeviceRef.current?.gatt?.connected) {
      return;
    }

    try {
      const response = await fetch(wifiTelemetryUrl, { cache: 'no-store' });
      if (!response.ok) {
        setWifiTelemetryStatus('unavailable');
        return;
      }

      const payload = await response.text();
      let parsedPayload = null;

      try {
        parsedPayload = parseWifiTelemetryPayload(JSON.parse(payload));
      } catch (error) {
        const fallbackText = payload.trim();
        if (fallbackText) {
          parsedPayload = {
            temperature: '',
            pressure: '',
            telemetry: fallbackText,
            status: 'Connected via Wi‑Fi'
          };
        }
      }

      if (!parsedPayload) {
        setWifiTelemetryStatus('idle');
        return;
      }

      const nextReadCount = bleReadCount + 1;
      setWifiTelemetryStatus('available');
      setBleTelemetry(parsedPayload.telemetry || '');
      setBleParsedTelemetry({
        temperature: parsedPayload.temperature || '',
        pressure: parsedPayload.pressure || ''
      });
      setBleReadCount(nextReadCount);
      setBleStatus(parsedPayload.status || 'Connected via Wi‑Fi');
      if (!bleConnectionRef.current.serialNumber && selectedSerialNumber) {
        bleConnectionRef.current = {
          serialNumber: selectedSerialNumber,
          deviceName: selectedSerialNumber
        };
      }
      syncSharedBleDeviceTelemetry({
        telemetry: parsedPayload.telemetry || '',
        parsedTelemetry: {
          temperature: parsedPayload.temperature || '',
          pressure: parsedPayload.pressure || ''
        },
        readCount: nextReadCount,
        status: parsedPayload.status || 'Connected via Wi‑Fi',
        connected: true
      });
    } catch (error) {
      setWifiTelemetryStatus('unavailable');
    }
  };

  const handleBleValue = (event) => {
    const receivedValue = event?.target?.value;
    const decodedValue = decodeBlePayload(receivedValue).trim();

    if (!decodedValue) {
      return;
    }

    if (bleConnected) {
      setBleStatus('Connected and streaming live data');
    }

    setBleTelemetry(decodedValue);
    const parsedTemperature = decodedValue.match(/Temperature:\s*([-+]?\d+(?:\.\d+)?)\s*C/i);
    const parsedPressure = decodedValue.match(/Pressure:\s*([-+]?\d+(?:\.\d+)?)\s*hPa/i);

    setBleParsedTelemetry({
      temperature: parsedTemperature ? `${parsedTemperature[1]} C` : '',
      pressure: parsedPressure ? `${parsedPressure[1]} hPa` : ''
    });

    const nextReadCount = bleReadCount + 1;
    setBleReadCount(nextReadCount);
    syncSharedBleDeviceTelemetry({
      telemetry: decodedValue,
      parsedTelemetry: {
        temperature: parsedTemperature ? `${parsedTemperature[1]} C` : '',
        pressure: parsedPressure ? `${parsedPressure[1]} hPa` : ''
      },
      readCount: nextReadCount,
      status: 'Connected and streaming live data'
    });
  };

  const handleWifiProvisionStatus = (event) => {
    const status = decodeBlePayload(event?.target?.value).trim();
    if (!status) {
      return;
    }

    setWifiProvisionStatus(status);
    setWifiProvisioning(status === 'connecting');
    if (status.startsWith('error:')) {
      setBleError(status.slice(6));
    } else if (status.startsWith('connected:')) {
      setBleError('');
      const deviceIp = status.slice('connected:'.length);
      if (/^\d{1,3}(\.\d{1,3}){3}$/.test(deviceIp)) {
        setWifiTelemetryUrl(`http://${deviceIp}/telemetry`);
      }
    }
  };

  const applyWifiCredentials = async () => {
    const service = bleCharacteristicRef.current?.service;
    const applyCharacteristic = wifiApplyCharacteristicRef.current;
    if (!service || !applyCharacteristic) {
      setBleError('Connect to the bucket over BLE before updating Wi-Fi.');
      return;
    }

    const ssid = wifiProvisionSsid.trim();
    if (!ssid) {
      setBleError('Enter a Wi-Fi network name.');
      return;
    }

    setBleError('');
    setWifiProvisionStatus('Sending Wi-Fi credentials...');
    setWifiProvisioning(true);

    try {
      const ssidCharacteristic = await service.getCharacteristic(WIFI_SSID_UUID);
      const passwordCharacteristic = await service.getCharacteristic(WIFI_PASSWORD_UUID);
      const encoder = new TextEncoder();
      await ssidCharacteristic.writeValueWithResponse(encoder.encode(ssid));
      await passwordCharacteristic.writeValueWithResponse(encoder.encode(wifiProvisionPassword));
      await applyCharacteristic.writeValueWithResponse(encoder.encode('apply'));
    } catch (error) {
      setWifiProvisioning(false);
      setWifiProvisionStatus('');
      setBleError(`Unable to send Wi-Fi credentials: ${error.message}`);
    }
  };

  const handleBleDisconnected = () => {
    if (bleSessionStartRef.current) {
      logDeviceOperation({
        operationStart: bleSessionStartRef.current,
        operationEnd: new Date().toISOString(),
        notes: bleTelemetry,
        source: 'ble'
      });
    }

    bleSessionStartRef.current = '';
    if (blePollingTimerRef.current) {
      clearInterval(blePollingTimerRef.current);
      blePollingTimerRef.current = null;
    }
    bleCharacteristicRef.current = null;
    wifiApplyCharacteristicRef.current = null;
    wifiStatusCharacteristicRef.current = null;
    clearSharedBleDeviceTelemetry();
    setBleConnected(false);
    setWifiProvisioning(false);
    setWifiProvisionStatus('');
    setBleStatus('Device disconnected');
    setBleDeviceName('');
    setBleTelemetry('');
    setBleParsedTelemetry({ temperature: '', pressure: '' });
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(CAMERA_URL_STORAGE_KEY, cameraUrl || DEFAULT_CAMERA_URL);
    }

    setStreamVisible(false);
    setStreamStatus('idle');
  }, [cameraUrl]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(WIFI_TELEMETRY_URL_STORAGE_KEY, wifiTelemetryUrl || DEFAULT_WIFI_TELEMETRY_URL);
    }
  }, [wifiTelemetryUrl]);

  useEffect(() => {
    const wifiModeActive = connectionMode === 'wifi' && wifiConnectionActive;
    if (!wifiTelemetryUrl || !wifiModeActive) {
      return undefined;
    }

    setWifiTelemetryStatus('checking');
    fetchLocalWifiTelemetry();
    const timer = setInterval(() => {
      void fetchLocalWifiTelemetry();
    }, 3000);

    return () => clearInterval(timer);
  }, [wifiTelemetryUrl, connectionMode, wifiConnectionActive]);

  useEffect(() => {
    if (connectionMode === 'wifi' && wifiConnectionActive) {
      setStreamStatus('checking');
      setStreamAttempt((count) => count + 1);
      setStreamVisible(true);
    } else {
      setStreamVisible(false);
      setStreamStatus('idle');
    }
  }, [connectionMode, wifiConnectionActive]);

  const stopRemoteShare = () => {
    relaySenderRef.current?.stop();
    relayCanvasRef.current?.stop();
    relaySenderRef.current = null;
    relayCanvasRef.current = null;
    setRelaySharing(false);
    setRelayViewerCount(0);
    setRelayStatus('');
  };

  const startRemoteShare = () => {
    const streamImage = document.querySelector('.camera-preview');
    if (!streamImage || !streamImage.complete || !streamImage.naturalWidth) {
      setRelayStatus('Wait until the camera picture is showing, then try again.');
      return;
    }

    if (!selectedSerialNumber) {
      setRelayStatus('Select a serial number first.');
      return;
    }

    if (typeof RTCPeerConnection === 'undefined' || !HTMLCanvasElement.prototype.captureStream) {
      setRelayStatus('This browser cannot share video. Use Chrome on Android.');
      return;
    }

    const canvasStream = createCanvasStream(streamImage, 30);
    relayCanvasRef.current = canvasStream;
    relaySenderRef.current = createRelaySender({
      serialNumber: selectedSerialNumber,
      customerId: foundCustomerId,
      stream: canvasStream.stream,
      onStatus: setRelayStatus,
      onViewerCount: setRelayViewerCount
    });
    setRelaySharing(true);
  };

  useEffect(() => () => {
    relaySenderRef.current?.stop();
    relayCanvasRef.current?.stop();
  }, []);

  const disconnectWifi = () => {
    stopRemoteShare();
    if (wifiSessionStartRef.current) {
      logDeviceOperation({
        operationStart: wifiSessionStartRef.current,
        operationEnd: new Date().toISOString(),
        notes: bleTelemetry,
        source: 'wifi'
      });
    }

    wifiSessionStartRef.current = '';
    lastCameraSnapshotRef.current = '';
    lastRecordingUrlRef.current = '';
    if (typeof window !== 'undefined') {
      localStorage.removeItem(WIFI_SESSION_START_STORAGE_KEY);
    }
    setWifiConnectionActive(false);
    setWifiTelemetryStatus('idle');
    clearSharedBleDeviceTelemetry();
    resetBleState();
  };

  const startWifiConnection = () => {
    if (!bleConnectionRef.current.serialNumber && selectedSerialNumber) {
      bleConnectionRef.current = {
        serialNumber: selectedSerialNumber,
        deviceName: selectedSerialNumber
      };
    }

    if (typeof window !== 'undefined' && selectedSerialNumber) {
      localStorage.setItem(WIFI_SERIAL_NUMBER_STORAGE_KEY, selectedSerialNumber);
    }

    wifiSessionStartRef.current = new Date().toISOString();
    if (typeof window !== 'undefined') {
      localStorage.setItem(WIFI_SESSION_START_STORAGE_KEY, wifiSessionStartRef.current);
    }

    setWifiModalOpen(false);
    setWifiConnectionActive(true);
  };

  const handleConnectionModeChange = async (nextMode) => {
    if (nextMode === connectionMode) {
      return;
    }

    if (bleConnected || bleDeviceRef.current?.gatt?.connected) {
      await disconnectBle();
    }

    if (wifiConnectionActive) {
      disconnectWifi();
    }

    setWifiModalOpen(false);
    setConnectionMode(nextMode);
  };

  const captureCameraSnapshot = async () => {
    if (!streamVisible) {
      return '';
    }

    const streamImage = document.querySelector('.camera-preview');
    if (!streamImage || !streamImage.complete || !streamImage.naturalWidth) {
      return '';
    }

    const snapshot = await buildCameraPreviewDataUrl({
      image: streamImage,
      temperature: bleParsedTelemetry.temperature || '--',
      pressure: bleParsedTelemetry.pressure || '--',
      status: bleStatus || 'idle',
      serialNumber: selectedSerialNumber || bleDeviceName || ''
    });

    if (snapshot) {
      lastCameraSnapshotRef.current = snapshot;
      syncSharedBleDeviceTelemetry({
        cameraSnapshotData: snapshot,
        status: bleStatus || 'Connected'
      });
    }

    return snapshot;
  };

  const handleRecordClip = async () => {
    if (isRecording) {
      return;
    }

    const streamImage = document.querySelector('.camera-preview');
    if (!streamImage || !streamImage.complete || !streamImage.naturalWidth) {
      setRecordingStatus('Open the camera stream before recording');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const context = canvas.getContext('2d');
    if (!context) {
      setRecordingStatus('Canvas capture is not available in this browser');
      return;
    }

    const stream = canvas.captureStream?.(30);
    if (!stream) {
      setRecordingStatus('This browser does not support recording canvas video');
      return;
    }

    const chunks = [];
    const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9' });
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    setIsRecording(true);
    setRecordingStatus('Recording 20s clip...');

    const drawFrame = async () => {
      if (!isRecording) {
        return;
      }

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(streamImage, 0, 0, canvas.width, canvas.height);
      context.fillStyle = 'rgba(8, 15, 24, 0.65)';
      context.fillRect(16, 16, 310, 120);
      context.font = 'bold 20px sans-serif';
      context.fillStyle = '#ffffff';
      const lines = [
        selectedSerialNumber ? `Device: ${selectedSerialNumber}` : 'Device: unknown',
        `Temp: ${bleParsedTelemetry.temperature || '--'}`,
        `Pressure: ${bleParsedTelemetry.pressure || '--'}`,
        `Status: ${bleStatus || 'idle'}`
      ];
      lines.forEach((line, index) => {
        context.fillText(line, 28, 42 + index * 24);
      });
    };

    const drawTicker = setInterval(() => {
      void drawFrame();
    }, 250);

    recorder.start();

    setTimeout(() => {
      clearInterval(drawTicker);
      recorder.stop();
    }, 20000);

    recorder.onstop = async () => {
      const clipBlob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
      const formData = new FormData();
      formData.append('file', clipBlob, `${selectedSerialNumber || 'device'}-clip.webm`);
      formData.append('serial_number', selectedSerialNumber || '');
      formData.append('customer_id', String(foundCustomerId || 0));
      formData.append('organization_code', organizationCode || '');
      formData.append('device_name', bleDeviceName || selectedSerialNumber || '');
      formData.append('status', 'Recorded 20s clip');
      formData.append('telemetry', bleTelemetry || '');
      formData.append('temperature', bleParsedTelemetry.temperature || '');
      formData.append('pressure', bleParsedTelemetry.pressure || '');

      try {
        const response = await fetchWithAuth(`${getApiBaseUrl()}/telemetry/clip`, {}, scopedAuthToken || localStorage.getItem(APP_AUTH_TOKEN_KEY));
        const isFileUploadRequest = response && response.headers;
        if (isFileUploadRequest) {
          // fetchWithAuth cannot send FormData because it adds JSON headers; use a direct request below.
        }

        const uploadResponse = await fetch(`${getApiBaseUrl()}/telemetry/clip`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${scopedAuthToken || localStorage.getItem(APP_AUTH_TOKEN_KEY) || localStorage.getItem(CONNECT_GUEST_TOKEN_KEY)}`
          },
          body: formData
        });

        const uploadData = await uploadResponse.json().catch(() => ({}));
        if (!uploadResponse.ok) {
          throw new Error(uploadData.error || 'Failed to upload recording');
        }

        setRecordingStatus('Clip uploaded successfully');
        setIsRecording(false);
        lastRecordingUrlRef.current = uploadData.recording_url || '';
        syncSharedBleDeviceTelemetry({
          recordingUrl: uploadData.recording_url || '',
          status: 'Recorded 20s clip'
        });
      } catch (error) {
        setRecordingStatus(error.message || 'Recording upload failed');
      } finally {
        setIsRecording(false);
      }
    };
  };

  useEffect(() => {
    return () => {
      if (blePollingTimerRef.current) {
        clearInterval(blePollingTimerRef.current);
      }

      if (bleDeviceRef.current?.gatt?.connected) {
        bleDeviceRef.current.gatt.disconnect();
      }

      clearSharedBleDeviceTelemetry();
    };
  }, []);

  const startBlePolling = () => {
    if (blePollingTimerRef.current) {
      clearInterval(blePollingTimerRef.current);
    }

    blePollingTimerRef.current = setInterval(async () => {
      const characteristic = bleCharacteristicRef.current;
      if (!characteristic) {
        return;
      }

      try {
        const currentValue = await characteristic.readValue();
        handleBleValue({ target: { value: currentValue } });
      } catch (error) {
        // Keep polling silently; notify stream might still recover.
      }
    }, 2500);
  };

  const disconnectBle = async () => {
    setBleDisconnecting(true);
    setBleError('');

    try {
      if (bleCharacteristicRef.current) {
        bleCharacteristicRef.current.removeEventListener('characteristicvaluechanged', handleBleValue);
      }
      if (wifiStatusCharacteristicRef.current) {
        wifiStatusCharacteristicRef.current.removeEventListener('characteristicvaluechanged', handleWifiProvisionStatus);
      }

      if (bleDeviceRef.current) {
        bleDeviceRef.current.removeEventListener('gattserverdisconnected', handleBleDisconnected);

        if (bleDeviceRef.current.gatt?.connected) {
          bleDeviceRef.current.gatt.disconnect();
        }
      }

      if (bleSessionStartRef.current) {
        logDeviceOperation({
          operationStart: bleSessionStartRef.current,
          operationEnd: new Date().toISOString(),
          notes: bleTelemetry,
          source: 'ble'
        });
        bleSessionStartRef.current = '';
      }
    } finally {
      bleDeviceRef.current = null;
      bleCharacteristicRef.current = null;
      wifiApplyCharacteristicRef.current = null;
      wifiStatusCharacteristicRef.current = null;
      setWifiProvisioning(false);
      setWifiProvisionStatus('');
      clearSharedBleDeviceTelemetry();
      resetBleState();
      setBleDisconnecting(false);
    }
  };

  const connectToBleDevice = async () => {
    setBleError('');
    setBleTelemetry('');
    setBleParsedTelemetry({ temperature: '', pressure: '' });
    setBleReadCount(0);

    if (!bleSupported) {
      setBleError('This browser does not support Web Bluetooth. Use Chrome or Edge on a secure origin.');
      return;
    }

    if (!navigator?.bluetooth?.requestDevice) {
      setBleError('Web Bluetooth is unavailable in this browser context.');
      return;
    }

    setBleStatus('Looking for a RemuBucket BLE device...');
    setBleConnecting(true);

    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [BLE_SERVICE_UUID] }]
      });

      setBleStatus(`Connecting to ${device.name || selectedSerialNumber || 'RemuBucket'}...`);
      device.addEventListener('gattserverdisconnected', handleBleDisconnected);

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(BLE_SERVICE_UUID);
      const characteristic = await service.getCharacteristic(BLE_CHARACTERISTIC_UUID);
      const applyCharacteristic = await service.getCharacteristic(WIFI_APPLY_UUID);
      const wifiStatusCharacteristic = await service.getCharacteristic(WIFI_STATUS_UUID);

      characteristic.addEventListener('characteristicvaluechanged', handleBleValue);
      await characteristic.startNotifications();
      wifiStatusCharacteristic.addEventListener('characteristicvaluechanged', handleWifiProvisionStatus);
      await wifiStatusCharacteristic.startNotifications();

      bleDeviceRef.current = device;
      bleCharacteristicRef.current = characteristic;
      wifiApplyCharacteristicRef.current = applyCharacteristic;
      wifiStatusCharacteristicRef.current = wifiStatusCharacteristic;
      bleConnectionRef.current = {
        serialNumber: selectedSerialNumber,
        deviceName: device.name || selectedSerialNumber || 'RemuBucket'
      };

      setBleConnected(true);
      setBleDeviceName(device.name || selectedSerialNumber || 'RemuBucket');
      setBleStatus('Connected. Waiting for live data...');
      setBleError('');
      setWifiProvisionStatus('');
      bleSessionStartRef.current = new Date().toISOString();
      syncSharedBleDeviceTelemetry({ status: 'Connected. Waiting for live data...' });

      startBlePolling();

      try {
        const initialValue = await characteristic.readValue();
        handleBleValue({ target: { value: initialValue } });
      } catch (initialReadError) {
        // Some BLE stacks reject immediate read after notification setup.
      }
      try {
        const initialWifiStatus = await wifiStatusCharacteristic.readValue();
        handleWifiProvisionStatus({ target: { value: initialWifiStatus } });
      } catch (initialStatusError) {
        // Status notifications still report the result of the next update.
      }
    } catch (error) {
      if (error?.name === 'NotFoundError' || error?.name === 'NetworkError' || error?.name === 'NotSupportedError') {
        resetBleState();
        clearSharedBleDeviceTelemetry();
        setBleStatus('Connection failed');
        setBleError('Device is out of reach');
      } else {
        resetBleState();
        clearSharedBleDeviceTelemetry();
        setBleStatus('Connection failed');
        setBleError(`✗ ${error.message}`);
      }
    } finally {
      setBleConnecting(false);
    }
  };

  const bleStatusTone = bleError ? 'error' : bleConnected ? 'success' : 'neutral';

  const handleOrganizationLookup = async () => {
    const trimmedOrganizationCode = organizationCode.trim();

    setOrganizationLookupMessage('');
    setOrganizationLookupError('');
    setFoundCustomerId(null);
    setBucketTypes([]);
    setSelectedBucketType('');
    setSerialNumbers([]);
    setSelectedSerialNumber('');
    setBucketDataError('');
    setScopedAuthToken(null);

    if (bleConnected || bleDeviceRef.current?.gatt?.connected) {
      await disconnectBle();
    } else {
      resetBleState();
    }

    if (!trimmedOrganizationCode) {
      setOrganizationLookupError('Organization code is required');
      return;
    }

    setOrganizationLookupLoading(true);

    try {
      let token = await getScopedToken(trimmedOrganizationCode);
      let response = await fetchWithAuth(
        `${getApiBaseUrl()}/customers/by-organization/${encodeURIComponent(trimmedOrganizationCode)}`
      , {}, token);

      if (response.status === 401 || response.status === 403) {
        token = await getOrganizationGuestToken(trimmedOrganizationCode);
        response = await fetchWithAuth(
          `${getApiBaseUrl()}/customers/by-organization/${encodeURIComponent(trimmedOrganizationCode)}`,
          {},
          token
        );
      }

      if (!response.ok) {
        if (response.status === 404) {
          setOrganizationLookupError('No customer found for this organization code');
          return;
        }

        const errorData = await parseJsonResponse(response);
        throw new Error(errorData.error || 'Failed to fetch customer ID');
      }

      const data = await parseJsonResponse(response);
      setScopedAuthToken(token);
      await cacheOrganizationCode(trimmedOrganizationCode, data.customer_id);

      setFoundCustomerId(data.customer_id);
      setOrganizationLookupMessage(`✓ Found customer ID ${data.customer_id}. Select a bucket type next.`);

      setBucketTypesLoading(true);
      try {
        const bucketsResponse = await fetchWithAuth(
          `${getApiBaseUrl()}/buckets/by-customer/${data.customer_id}`,
          {},
          token
        );

        if (!bucketsResponse.ok) {
          const bucketsError = await parseJsonResponse(bucketsResponse);
          throw new Error(bucketsError.error || 'Failed to fetch bucket types');
        }

        const bucketsData = await parseJsonResponse(bucketsResponse);
        const uniqueBucketTypes = Array.from(
          new Set((bucketsData.buckets || []).map((bucket) => bucket.bucket_type).filter(Boolean))
        );

        setBucketTypes(uniqueBucketTypes);
      } finally {
        setBucketTypesLoading(false);
      }
    } catch (error) {
      setOrganizationLookupError(`✗ ${error.message}`);
    } finally {
      setOrganizationLookupLoading(false);
    }
  };

  const handleBucketTypeChange = async (event) => {
    const bucketType = event.target.value;

    setSelectedBucketType(bucketType);
    setSelectedSerialNumber('');
    setSerialNumbers([]);
    setBucketDataError('');

    if (bleConnected || bleDeviceRef.current?.gatt?.connected) {
      await disconnectBle();
    } else {
      resetBleState();
    }

    if (!bucketType || !foundCustomerId) {
      return;
    }

    setSerialNumbersLoading(true);
    try {
      const serialsResponse = await fetchWithAuth(
        `${getApiBaseUrl()}/buckets/serial-numbers/by-type/${encodeURIComponent(bucketType)}?customerId=${foundCustomerId}`,
        {},
        scopedAuthToken
      );

      if (!serialsResponse.ok) {
        const serialsError = await parseJsonResponse(serialsResponse);
        throw new Error(serialsError.error || 'Failed to fetch serial numbers');
      }

      const serialsData = await parseJsonResponse(serialsResponse);
      setSerialNumbers(serialsData.serial_numbers || []);
    } catch (error) {
      setBucketDataError(`✗ ${error.message}`);
      setSerialNumbers([]);
    } finally {
      setSerialNumbersLoading(false);
    }
  };

  const showConnectDetails =
    foundCustomerId !== null || bleConnected || wifiConnectionActive || Boolean(selectedSerialNumber);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Connect</h1>        
        <div className="build-stamp">Build stamp: {CONNECT_BUILD_STAMP}</div>
      </div>

      <div className="content-grid">
        <div className="card">
          <h2>Connect Bucket Device</h2>

          <div className="form-group">
            <label htmlFor="connectionMode">Connection type</label>
            <select
              id="connectionMode"
              name="connectionMode"
              value={connectionMode}
              onChange={(event) => handleConnectionModeChange(event.target.value)}
            >
              <option value="ble">Bluetooth (BLE)</option>
              <option value="wifi">Wi‑Fi (camera + local data)</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="organizationCode">Organization code</label>
            <input
              type="text"
              id="organizationCode"
              name="organizationCode"
              value={organizationCode}
              onChange={(event) => setOrganizationCode(event.target.value)}
              placeholder="Enter organization code"
            />
          </div>

          <button
            type="button"
            onClick={handleOrganizationLookup}
            disabled={organizationLookupLoading}
            className="btn btn-primary"
          >
            {organizationLookupLoading ? 'Looking up...' : 'Get Bucket Types'}
          </button>

          {organizationLookupMessage && <div className="message success">{organizationLookupMessage}</div>}
          {organizationLookupError && <div className="message error">{organizationLookupError}</div>}

          {foundCustomerId && (
            <div className="lookup-result">
              Matched customer ID: <strong>{foundCustomerId}</strong>
            </div>
          )}

          {showConnectDetails && (<>
          <div className="form-group">
            <label htmlFor="bucketType">Bucket type</label>
            <select
              id="bucketType"
              name="bucketType"
              value={selectedBucketType}
              onChange={handleBucketTypeChange}
              disabled={!foundCustomerId || bucketTypesLoading}
            >
              <option value="">
                {bucketTypesLoading ? 'Loading bucket types...' : 'Select bucket type'}
              </option>
              {bucketTypes.map((bucketType) => (
                <option key={bucketType} value={bucketType}>
                  {bucketType}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="serialNumber">Serial number</label>
            <select
              id="serialNumber"
              name="serialNumber"
              value={selectedSerialNumber}
              onChange={(event) => setSelectedSerialNumber(event.target.value)}
              disabled={!selectedBucketType || serialNumbersLoading}
            >
              <option value="">
                {serialNumbersLoading ? 'Loading serial numbers...' : 'Select serial number'}
              </option>
              {serialNumbers.map((serialNumber) => (
                <option key={serialNumber} value={serialNumber}>
                  {serialNumber}
                </option>
              ))}
            </select>
          </div>

          <div className="connect-actions">
            {connectionMode === 'ble' ? (
              <button
                type="button"
                onClick={bleConnected ? disconnectBle : connectToBleDevice}
                disabled={bleConnecting || bleDisconnecting || wifiProvisioning}
                className={`btn btn-primary ${bleConnected ? 'btn-secondary' : ''}`}
              >
                {bleDisconnecting
                  ? 'Disconnecting...'
                  : bleConnecting
                    ? 'Connecting...'
                    : bleConnected
                      ? 'Disconnect'
                      : 'Choose BLE Device'}
              </button>
            ) : (
              <button
                type="button"
                onClick={wifiConnectionActive ? disconnectWifi : () => setWifiModalOpen(true)}
                disabled={!selectedSerialNumber && !wifiConnectionActive}
                className={`btn btn-primary ${wifiConnectionActive ? 'btn-secondary' : ''}`}
              >
                {wifiConnectionActive ? 'Disconnect Wi‑Fi' : 'Connect via Wi‑Fi'}
              </button>
            )}
          </div>

          <div className={`status-panel status-${bleStatusTone}`}>
            <div className="status-title">{bleStatus}</div>
            <div className="status-details">
              {bleSupported ? 'Web Bluetooth available' : 'Web Bluetooth not available in this browser'}
              {bleDeviceName ? ` • ${bleDeviceName}` : selectedSerialNumber ? ` • ${selectedSerialNumber}` : ''}
            </div>
          </div>

          {connectionMode === 'wifi' && !wifiConnectionActive && (
            <div className="status-panel status-neutral">
              Press Connect via Wi‑Fi to choose your local network and camera settings.
            </div>
          )}
          </>)}

          {connectionMode === 'wifi' && wifiConnectionActive && (
            <div className="camera-stream-panel">
              <div className="telemetry-label">Camera stream</div>

              <div className={`status-panel status-${wifiTelemetryStatus === 'available' ? 'success' : wifiTelemetryStatus === 'unavailable' ? 'error' : 'neutral'}`}>
                {wifiTelemetryStatus === 'checking' && 'Checking local Wi‑Fi telemetry...'}
                {wifiTelemetryStatus === 'available' && 'Local Wi‑Fi telemetry is live'}
                {wifiTelemetryStatus === 'unavailable' && 'Unable to reach the local Wi‑Fi telemetry endpoint'}
                {wifiTelemetryStatus === 'idle' && 'Local Wi‑Fi telemetry is waiting for a response'}
              </div>

              <div className={`status-panel status-${streamStatus === 'available' ? 'success' : streamStatus === 'unavailable' ? 'error' : 'neutral'} camera-stream-status`}>
                {streamStatus === 'checking' && 'Checking stream...'}
                {streamStatus === 'available' && 'Stream is live'}
                {streamStatus === 'unavailable' && (
                  <>
                    Browser blocked the embedded preview (this page is HTTPS, the camera is HTTP on your local
                    network), or this browser cannot render the MJPEG stream inline. Use the button below to open the
                    compatible viewer instead.
                  </>
                )}
              </div>

              <img
                key={streamAttempt}
                className="camera-preview"
                src={ensureUrlScheme(cameraUrl)}
                alt="ESP32 camera preview"
                crossOrigin="anonymous"
                onLoad={async () => {
                  setStreamStatus('available');
                  await captureCameraSnapshot();
                }}
                onError={() => setStreamStatus('unavailable')}
              />

              <div className="camera-actions-row">
                <button
                  type="button"
                  onClick={() => window.open(`${window.location.origin}/connect/stream`, '_blank', 'noopener,noreferrer')}
                  disabled={!cameraUrl}
                  className="btn btn-secondary camera-stream-open-tab"
                >
                  Open Stream + Data in New Tab
                </button>

                <button
                  type="button"
                  onClick={handleRecordClip}
                  disabled={!cameraUrl || isRecording}
                  className="btn btn-primary camera-record-button"
                >
                  {isRecording ? 'Recording 20s...' : 'Record 20s Clip'}
                </button>
              </div>

              <div className="camera-actions-row">
                <button
                  type="button"
                  onClick={relaySharing ? stopRemoteShare : startRemoteShare}
                  disabled={streamStatus !== 'available'}
                  className="btn btn-secondary camera-stream-open-tab"
                >
                  {relaySharing ? 'Stop sharing' : 'Share live video with remote viewer'}
                </button>
              </div>

              {relayStatus && (
                <div className="message success">
                  {relayStatus}
                  {relaySharing ? ` Viewers connected: ${relayViewerCount}.` : ''}
                </div>
              )}

              {recordingStatus && <div className="message success">{recordingStatus}</div>}
            </div>
          )}

          {(bleConnected || bleTelemetry) && (
            <div className="telemetry-box">
              <div className="telemetry-label">Live data</div>
              <div className="live-metrics">
                <div className="live-metric">
                  <span className="metric-name">Temperature</span>
                  <span className="metric-value">{bleParsedTelemetry.temperature || '--'}</span>
                </div>
                <div className="live-metric">
                  <span className="metric-name">Pressure</span>
                  <span className="metric-value">{bleParsedTelemetry.pressure || '--'}</span>
                </div>
              </div>
              <pre>{bleTelemetry || 'Waiting for telemetry payload...'}</pre>
              <div className="telemetry-meta">Notifications received: {bleReadCount}</div>
            </div>
          )}

          {bleError && <div className="message error">{bleError}</div>}
          {bucketDataError && <div className="message error">{bucketDataError}</div>}

          {connectionMode === 'ble' && (
            <div className="wifi-provisioning">
              <h3>Configure device Wi-Fi</h3>
              <div className="form-group">
                <label htmlFor="wifiProvisionSsid">Wi-Fi network name</label>
                <input
                  id="wifiProvisionSsid"
                  type="text"
                  autoComplete="off"
                  value={wifiProvisionSsid}
                  onChange={(event) => setWifiProvisionSsid(event.target.value)}
                  maxLength={32}
                  disabled={wifiProvisioning}
                />
              </div>
              <div className="form-group">
                <label htmlFor="wifiProvisionPassword">Wi-Fi password</label>
                <input
                  id="wifiProvisionPassword"
                  type="password"
                  autoComplete="new-password"
                  value={wifiProvisionPassword}
                  onChange={(event) => setWifiProvisionPassword(event.target.value)}
                  maxLength={63}
                  disabled={wifiProvisioning}
                />
              </div>
              <button
                type="button"
                onClick={applyWifiCredentials}
                disabled={!bleConnected || wifiProvisioning || !wifiProvisionSsid.trim()}
                className="btn btn-primary"
              >
                {wifiProvisioning ? 'Connecting device...' : bleConnected ? 'Save and connect' : 'Connect to a BLE device first'}
              </button>
              {wifiProvisionStatus && (
                <div className={`status-panel ${wifiProvisionStatus.startsWith('error:') ? 'status-error' : wifiProvisionStatus.startsWith('connected:') ? 'status-success' : 'status-neutral'}`}>
                  {wifiProvisionStatus.startsWith('connected:')
                    ? `Connected to Wi-Fi (${wifiProvisionStatus.slice('connected:'.length)})`
                    : wifiProvisionStatus.startsWith('error:')
                      ? wifiProvisionStatus.slice('error:'.length)
                      : wifiProvisionStatus === 'connecting'
                        ? 'Connecting to the selected Wi-Fi network...'
                        : wifiProvisionStatus}
                </div>
              )}
            </div>
          )}

          {showConnectDetails && (
          <div className="helper-text">
            Wi-Fi setup works without a serial-number lookup. Choose a BLE device that exposes the RemuBucket service;
            select a bucket serial number as well if you want telemetry associated with that bucket in the portal.
          </div>
                      )}
                    </div>
      </div>

      {wifiModalOpen && (
        <div className="wifi-modal-overlay" role="dialog" aria-modal="true">
          <div className="wifi-modal">
            <h3>Connect via Wi‑Fi</h3>
            <p className="helper-text">
              Browsers cannot switch Wi‑Fi networks automatically. Join the same Wi‑Fi network as the bucket (or
              <strong> RemuBucket-AP</strong>) in your device's settings first. If you connected over Bluetooth, the
              telemetry address is filled in for you; the camera address must be entered by hand.
            </p>

            <label htmlFor="wifiTelemetryUrl" className="camera-url-label">Local Wi‑Fi telemetry URL</label>
            <input
              id="wifiTelemetryUrl"
              type="text"
              value={wifiTelemetryUrl}
              onChange={(event) => setWifiTelemetryUrl(event.target.value.trim() || DEFAULT_WIFI_TELEMETRY_URL)}
              placeholder="http://192.168.4.1/telemetry"
            />

            <label htmlFor="cameraUrl" className="camera-url-label">Camera stream URL</label>
            <input
              id="cameraUrl"
              type="text"
              value={cameraUrl}
              onChange={(event) => setCameraUrl(event.target.value.trim() || DEFAULT_CAMERA_URL)}
              placeholder="http://192.168.4.2:81/stream"
            />

            <div className="wifi-modal-actions">
              <button type="button" onClick={() => setWifiModalOpen(false)} className="btn btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={startWifiConnection}
                disabled={!selectedSerialNumber}
                className="btn btn-primary"
              >
                Start Connection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Connect;